import { PaginatedResponse } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';

import { registerResolveFunction } from './resolve-function-registry';
import { seed } from './resolve-slice';

type ResolveEventDetail = string | { id: string; skipTimeout?: boolean };

function parseResolveDetail(detail: unknown): { id: string; skipTimeout: boolean } | null {
    if (typeof detail === 'string') {
        return { id: detail, skipTimeout: false };
    }
    if (detail && typeof detail === 'object' && typeof (detail as { id?: unknown }).id === 'string') {
        const record = detail as { id: string; skipTimeout?: boolean };
        return { id: record.id, skipTimeout: record.skipTimeout === true };
    }
    return null;
}

export function useResolver<T extends { id: string }>(
    key: string,
    event: keyof WindowEventMap,
    type: EntityType,
    resolveAPI: (ids: string[]) => Promise<PaginatedResponse<T>>,
    dispatch: ThunkDispatch<any, any, Action>
) {
    const [pending, changePending] = useState<string[]>([]);
    const [timeoutSet, changeTimeoutSet] = useState<boolean>(false);
    const [timeoutFinished, changeTimeoutFinished] = useState<boolean>(false);

    useEffect(() => {
        window.addEventListener(event, (e) => listener(e as CustomEvent<ResolveEventDetail>));
    }, []);

    useEffect(() => {
        registerResolveFunction(type, (id, options) => {
            const detail: ResolveEventDetail = options?.skipTimeout ? { id, skipTimeout: true } : id;
            window.dispatchEvent(new CustomEvent(event, { detail: detail as never }));
            return key + '_loading';
        });
    }, [type, event, key]);

    useEffect(() => {
        if (timeoutFinished) {
            const stored = localStorage.getItem(key);
            if (stored != null) {
                changePending(JSON.parse(stored));
                localStorage.removeItem(key);
            }
            changeTimeoutSet(false);
            changeTimeoutFinished(false);
        }
    }, [timeoutFinished]);

    useEffect(() => {
        if (pending.length > 0) {
            resolveAPI(pending).then((result) => {
                dispatch(seed({ key: type, value: result.results }));
                changePending([]);
                changeTimeoutSet(false);
                changeTimeoutFinished(false);
            });
        }
    }, [pending]);

    useEffect(() => {
        if (timeoutSet || pending.length > 0) {
            localStorage.setItem(key + '_loading', JSON.stringify(true));
        } else {
            localStorage.removeItem(key + '_loading');
        }
    }, [timeoutSet, pending]);

    const listener = (event: CustomEvent<ResolveEventDetail>) => {
        const parsed = parseResolveDetail(event.detail);
        if (!parsed) {
            return;
        }

        const stored = localStorage.getItem(key);
        if (stored == null) {
            localStorage.setItem(key, JSON.stringify([parsed.id]));
        } else if (!JSON.parse(stored).includes(parsed.id)) {
            localStorage.setItem(key, JSON.stringify([...JSON.parse(stored), parsed.id]));
        }

        // Set busy synchronously so awaitResolve (especially skipTimeout) observes it
        // before React has flushed timeoutSet/pending effects.
        localStorage.setItem(key + '_loading', JSON.stringify(true));

        if (parsed.skipTimeout) {
            changeTimeoutSet(true);
            changeTimeoutFinished(true);
            return;
        }

        if (timeoutSet === false) {
            changeTimeoutSet(true);
            setTimeout(() => {
                changeTimeoutFinished(true);
            }, 1000);
        }
    };
}
